import React, { useState, useEffect, useCallback } from 'react';
import { attendanceAPI, holidayAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Home,
  Check,
  X,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EmployeeAttendance = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({ attendance: {}, holidays: {} });
  const [todayStatus, setTodayStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMarkModalOpen, setIsMarkModalOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  
  const [markData, setMarkData] = useState({
    status: 'present',
    is_wfh: false,
  });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const fetchCalendarData = useCallback(async () => {
    try {
      const response = await attendanceAPI.getCalendar(year, month);
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    }
  }, [year, month]);

  const fetchTodayStatus = useCallback(async () => {
    try {
      const response = await attendanceAPI.getTodayStatus();
      setTodayStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch today status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendarData();
    fetchTodayStatus();
  }, [fetchCalendarData, fetchTodayStatus]);

  const handleMarkAttendance = async () => {
    setMarking(true);
    try {
      // Request GPS location
      let latitude = null, longitude = null, address = null;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
        );
        latitude = pos.coords.latitude;
        longitude = pos.coords.longitude;
      } catch (e) {
        // GPS not available — continue without it
      }
      await attendanceAPI.markAttendance({ status: markData.status, is_wfh: markData.is_wfh, latitude, longitude, address });
      await fetchTodayStatus();
      await fetchCalendarData();
      setIsMarkModalOpen(false);
    } catch (error) {
      console.error('Failed to mark attendance:', error);
      alert(error.response?.data?.detail || 'Failed to mark attendance');
    } finally {
      setMarking(false);
    }
  };

  const handleCheckout = async () => {
    setMarking(true);
    try {
      let latitude = null, longitude = null, address = null;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
        );
        latitude = pos.coords.latitude;
        longitude = pos.coords.longitude;
      } catch (e) {
        // GPS not available
      }
      await attendanceAPI.checkout({ latitude, longitude, address });
      await fetchTodayStatus();
      await fetchCalendarData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to check out');
    } finally {
      setMarking(false);
    }
  };

  const openMarkModal = () => {
    if (todayStatus && todayStatus.status !== 'not_marked') {
      setMarkData({
        status: todayStatus.status,
        is_wfh: todayStatus.is_wfh || false,
      });
    } else {
      setMarkData({
        status: 'present',
        is_wfh: false,
      });
    }
    setIsMarkModalOpen(true);
  };

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
  };

  const getDaysInMonth = () => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = () => {
    return new Date(year, month - 1, 1).getDay();
  };

  const getStatusColor = (dateStr) => {
    const attendance = calendarData.attendance[dateStr];
    const isHoliday = calendarData.holidays[dateStr];
    
    if (isHoliday) return 'bg-slate-200 text-slate-600';
    if (!attendance) return 'bg-white';
    
    switch (attendance.status) {
      case 'present':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'absent':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'half_day_morning':
      case 'half_day_afternoon':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'on_leave':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      default:
        return 'bg-white';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present': return 'Present';
      case 'absent': return 'Absent';
      case 'half_day_morning': return 'Half Day (Morning)';
      case 'half_day_afternoon': return 'Half Day (Afternoon)';
      case 'on_leave': return 'On Leave';
      case 'not_marked': return 'Not Marked';
      default: return status;
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth();
    const firstDay = getFirstDayOfMonth();
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Day headers
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={`header-${i}`} className="text-center text-xs font-semibold text-slate-500 py-2">
          {dayNames[i]}
        </div>
      );
    }
    
    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }
    
    // Days of the month
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isToday = dateStr === todayStr;
      const attendance = calendarData.attendance[dateStr];
      const holidayName = calendarData.holidays[dateStr];
      const statusColor = getStatusColor(dateStr);
      
      days.push(
        <div
          key={day}
          className={`relative p-2 min-h-[60px] md:min-h-[80px] rounded-lg border ${statusColor} ${
            isToday ? 'ring-2 ring-[#1D35A0]' : ''
          } transition-all`}
          data-testid={`calendar-day-${day}`}
        >
          <span className={`text-sm font-medium ${isToday ? 'text-[#1D35A0]' : ''}`}>
            {day}
          </span>
          {holidayName && (
            <div className="text-[10px] text-slate-500 truncate mt-1" title={holidayName}>
              {holidayName}
            </div>
          )}
          {attendance && !holidayName && (
            <div className="mt-1">
              <div className="text-[10px] font-medium truncate">
                {getStatusLabel(attendance.status).split(' ')[0]}
              </div>
              {attendance.check_in_time && (
                <div className="text-[9px] text-slate-500 leading-tight">
                  <span className="text-green-600">{attendance.check_in_time}</span>
                  {attendance.check_out_time && (
                    <span className="text-red-500"> - {attendance.check_out_time}</span>
                  )}
                </div>
              )}
              {attendance.is_late && (
                <span className="text-[8px] text-orange-600 font-semibold">Late</span>
              )}
              {attendance.is_wfh && (
                <Home className="w-3 h-3 inline-block text-blue-500" title="Work From Home" />
              )}
            </div>
          )}
        </div>
      );
    }
    
    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="employee-attendance-page">
      <button onClick={() => navigate('/portal')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1D35A0] transition-colors mb-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Home
      </button>
      {/* Today's Status Card */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm" data-testid="today-status-card">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-[#1D35A0]">Today's Attendance</h2>
              <p className="text-slate-500 text-xs sm:text-sm mt-1">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {todayStatus && todayStatus.status !== 'not_marked' && todayStatus.check_in_time && !todayStatus.check_out_time && (
                <Button
                  onClick={handleCheckout}
                  variant="outline"
                  size="sm"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  disabled={marking}
                  data-testid="checkout-btn"
                >
                  <X className="w-4 h-4 mr-1.5" />
                  {marking ? 'Checking out...' : 'Check Out'}
                </Button>
              )}
              <Button
                onClick={openMarkModal}
                size="sm"
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                data-testid="mark-attendance-btn"
              >
                <Clock className="w-4 h-4 mr-1.5" />
                {todayStatus && todayStatus.status !== 'not_marked' ? 'Update' : 'Check In'}
              </Button>
            </div>
          </div>

          {/* Status & Time Info */}
          {todayStatus && todayStatus.status !== 'not_marked' ? (
            <div className="flex flex-wrap items-center gap-2">
              <div className={`px-3 py-1.5 rounded-lg text-sm ${
                todayStatus.status === 'present' ? 'bg-green-100 text-green-700' :
                todayStatus.status === 'absent' ? 'bg-red-100 text-red-700' :
                todayStatus.status.includes('half_day') ? 'bg-yellow-100 text-yellow-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                <span className="font-semibold">{getStatusLabel(todayStatus.status)}</span>
                {todayStatus.is_wfh && <span className="ml-1.5 text-xs">(WFH)</span>}
              </div>
              {todayStatus.check_in_time && (
                <div className="text-xs sm:text-sm text-slate-600">
                  <Clock className="w-3.5 h-3.5 inline mr-1" />
                  In: {todayStatus.check_in_time}
                  {todayStatus.check_out_time && ` · Out: ${todayStatus.check_out_time}`}
                </div>
              )}
              {todayStatus.is_late && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Late</span>
              )}
            </div>
          ) : (
            <div className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-sm w-fit">
              <AlertCircle className="w-4 h-4 inline mr-1.5" />
              Not marked yet
            </div>
          )}
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-[#1D35A0]">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevMonth} data-testid="prev-month-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={nextMonth} data-testid="next-month-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
            <span className="text-slate-600">Present</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
            <span className="text-slate-600">Absent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300"></div>
            <span className="text-slate-600">Half Day</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-100 border border-blue-300"></div>
            <span className="text-slate-600">On Leave</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-slate-200"></div>
            <span className="text-slate-600">Holiday</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1" data-testid="attendance-calendar">
          {renderCalendar()}
        </div>
      </div>

      {/* Mark Attendance Modal */}
      <Dialog open={isMarkModalOpen} onOpenChange={setIsMarkModalOpen}>
        <DialogContent className="max-w-md" data-testid="mark-attendance-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              Mark Attendance
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Record your attendance for today
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select
                value={markData.status}
                onValueChange={(value) => setMarkData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day_morning">Half Day (Morning)</SelectItem>
                  <SelectItem value="half_day_afternoon">Half Day (Afternoon)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {todayStatus?.check_in_time && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                <Clock className="w-4 h-4 inline mr-1.5 text-slate-400" />
                Checked in at <span className="font-medium text-[#1D35A0]">{todayStatus.check_in_time}</span>
                {todayStatus.check_out_time && (
                  <> · Checked out at <span className="font-medium text-[#1D35A0]">{todayStatus.check_out_time}</span></>
                )}
                <p className="text-xs text-slate-400 mt-1">Times are auto-captured by the system</p>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_wfh"
                checked={markData.is_wfh}
                onCheckedChange={(checked) => setMarkData(prev => ({ ...prev, is_wfh: checked }))}
                data-testid="checkbox-wfh"
              />
              <Label htmlFor="is_wfh" className="text-sm font-normal cursor-pointer">
                <Home className="w-4 h-4 inline mr-1 text-blue-500" />
                Work From Home (WFH)
              </Label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsMarkModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleMarkAttendance}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={marking}
                data-testid="submit-attendance-btn"
              >
                {marking ? 'Saving...' : 'Save Attendance'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeAttendance;
